import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexEntryType, type RolodexLoadedEntryJson } from 'types'

export interface MatchedDatapoint {
  id: string
  pi_tag: string
  name: string
  description: string
  /** Historian grouping (e.g. `Genset_7`, `BOP`) — the disambiguator when one
   * template point exists per unit under the same name. */
  point_source?: string
}

export interface RawDatapointData {
  name?: string
  pi_tag?: string
  description?: string
  point_source?: string
}

export type DatapointEntry = RolodexLoadedEntryJson & {
  entry: { data: RawDatapointData }
}

export function parseDatapointEntry(
  wrapper: DatapointEntry
): MatchedDatapoint | null {
  const { id, data } = wrapper.entry
  const piTag = data.pi_tag ?? data.name
  if (!id || !piTag) return null
  return {
    id,
    pi_tag: piTag,
    name: data.name ?? piTag,
    description: data.description ?? '',
    point_source: data.point_source || undefined,
  }
}

/**
 * Loads the site's DATA_POINTS Rolodex entries and parses them into
 * {@link MatchedDatapoint}s, with an id lookup map for resolving linked
 * datapoint ids.
 */
export function useSiteDatapoints(siteId: string | undefined): {
  datapoints: MatchedDatapoint[]
  datapointsById: Map<string, MatchedDatapoint>
  isLoading: boolean
} {
  const loadOptions = useMemo(
    () =>
      siteId
        ? {
            type: [RolodexEntryType.DATA_POINTS],
            tags: { site: [siteId] },
            limit: 10000,
          }
        : undefined,
    [siteId]
  )
  const { data: entries, isLoading } =
    useGetRolodexEntriesByLoadOptions<DatapointEntry[]>(loadOptions)

  const datapoints = useMemo(() => {
    const parsed: MatchedDatapoint[] = []
    for (const wrapper of entries ?? []) {
      const datapoint = parseDatapointEntry(wrapper)
      if (datapoint) parsed.push(datapoint)
    }
    return parsed
  }, [entries])

  const datapointsById = useMemo(
    () => new Map(datapoints.map((datapoint) => [datapoint.id, datapoint])),
    [datapoints]
  )

  return { datapoints, datapointsById, isLoading }
}
